import { clamp } from 'lodash-es';

import { firstLineLeftOf } from '../draft/blocks';
import type { ClauseDraft, DraftBlock, DraftList, DraftParagraph, IndentLevels } from '../draft/types';

const PT_PER_LEVEL = 36;
const PT_PER_QUARTER_LEVEL = PT_PER_LEVEL / 4;
const MAX_INDENT_LEVEL = 8;

const levelOf = (pt = 0) => clamp(Math.round(pt / PT_PER_LEVEL), 0, MAX_INDENT_LEVEL);

const quartersOf = (pt: number) => Math.round(pt / PT_PER_QUARTER_LEVEL);

const levelsOf = ({ indent: { left, hanging = 0 }, role }: DraftParagraph): IndentLevels => {
  const indentLevel = levelOf(left);
  const hang = role.kind === 'heading' && hanging > 0 ? Math.max(1, levelOf(hanging)) : levelOf(hanging);
  return { indentLevel, hanging: Math.min(hang, indentLevel) };
};

const listIndentOf = (
  { marker, items }: DraftList,
  parent?: DraftList,
): Pick<DraftList, 'textOffset' | 'indentLevel'> => {
  const first = items[0].blocks[0];
  if (marker.format === 'bullet' || first.kind !== 'paragraph') return {};
  const { left, hanging = 0 } = first.indent;
  if (left !== undefined && hanging > 0) {
    return parent && parent.textOffset === undefined ? {} : { textOffset: quartersOf(left) / 4 };
  }
  return parent ? {} : { indentLevel: left === undefined ? first.numbering?.level : levelOf(left) };
};

const quantise = (blocks: DraftBlock[], itemTextColumn?: number, parent?: DraftList): DraftBlock[] =>
  blocks.map((block): DraftBlock => {
    switch (block.kind) {
      case 'paragraph':
        return {
          ...block,
          indentLevels:
            itemTextColumn === undefined
              ? levelsOf(block)
              : { indentLevel: levelOf((firstLineLeftOf(block) ?? 0) - itemTextColumn), hanging: 0 },
        };
      case 'table':
        return {
          ...block,
          rows: block.rows.map((cells) => cells.map((cell) => ({ ...cell, blocks: quantise(cell.blocks) }))),
        };
      case 'list': {
        const list = { ...block, ...listIndentOf(block, parent) };
        return {
          ...list,
          items: list.items.map(({ blocks: [own, ...inside], ...item }) => ({
            ...item,
            blocks: [own, ...quantise(inside, own.kind === 'paragraph' ? own.indent.left : undefined, list)],
          })),
        };
      }
    }
  });

export const quantiseIndents = (draft: ClauseDraft): ClauseDraft => ({
  ...draft,
  blocks: quantise(draft.blocks),
});
